import { getPageFromEntry } from 'components/storefront/editor/utils/easyblocks';
import { isObject } from 'utils';

const EXCLUDED_SECTION_SETTINGS_FIELDS = Object.freeze([
  '_id',
  '_component',
  'Blocks',
  'custom_css',
  'disabled',
]);
const EXCLUDED_BLOCK_SETTINGS_FIELDS = Object.freeze([
  '_id',
  '_component',
  'disabled',
]);

class ConvertSectionGroupError extends Error {
  constructor(message, sectionGroupLabel) {
    super(`Failed to build ${sectionGroupLabel} config: ${message}`);
  }
}

function getUniqueItemName(itemName, allItems) {
  let uniqueItemName = itemName;
  let index = 1;

  while (uniqueItemName in allItems) {
    uniqueItemName = `${itemName}_${index}`;
    index++;
  }

  return uniqueItemName;
}

function getSectionName(section, allSections) {
  const { _id } = section;

  const parts = _id.split('__');
  const sectionName = parts[parts.length - 1];

  return getUniqueItemName(sectionName, allSections);
}

function getSectionType(section) {
  return section._component;
}

function getBlockName(block, allBlocks) {
  const { _id: blockId, _component: blockComponent } = block;
  const blockName = blockId.includes('__')
    ? blockId.split('__')[1]
    : blockComponent.split('__')[2];

  return getUniqueItemName(blockName, allBlocks);
}

function getBlockType(block, sectionComponent) {
  const { _component: blockComponent } = block;

  return blockComponent.replace(`Block__${sectionComponent}__`, '');
}

function getSettingsValue(value) {
  if (isObject(value)) {
    return value.value ?? null;
  }

  return value ?? null;
}

function getBlockSettings(block) {
  return Object.entries(block).reduce((acc, [key, value]) => {
    if (!EXCLUDED_BLOCK_SETTINGS_FIELDS.includes(key)) {
      acc[key] = getSettingsValue(value);
    }

    return acc;
  }, {});
}

function getSectionBlocks(section) {
  const { _component: sectionComponent, Blocks: blocks } = section;
  const hasBlocks = blocks?.length > 0;

  if (!hasBlocks) {
    return {};
  }

  return blocks.reduce(
    (acc, block) => {
      const blockName = getBlockName(block, acc.blocks);
      const blockType = getBlockType(block, sectionComponent);

      acc.blocks[blockName] = {
        type: blockType,
        disabled: getSettingsValue(block.disabled),
        settings: getBlockSettings(block),
      };

      acc.block_order.push(blockName);

      return acc;
    },
    { blocks: {}, block_order: [] },
  );
}

function getSectionSettings(section) {
  return Object.entries(section).reduce((acc, [key, value]) => {
    if (!EXCLUDED_SECTION_SETTINGS_FIELDS.includes(key)) {
      acc[key] = getSettingsValue(value);
    }

    return acc;
  }, {});
}

function convertSections(sectionGroup) {
  return sectionGroup.reduce(
    (acc, section) => {
      const sectionName = getSectionName(section, acc.sections);

      acc.sections[sectionName] = {
        type: getSectionType(section),
        disabled: getSettingsValue(section.disabled),
        custom_css: getSettingsValue(section.custom_css),
        settings: getSectionSettings(section),
        ...getSectionBlocks(section),
      };

      acc.order.push(sectionName);

      return acc;
    },
    { sections: {}, order: [] },
  );
}

export function convertSectionGroup(entry, sectionGroupData) {
  const { label: sectionGroupLabel, prop: sectionGroupProp } = sectionGroupData;

  if (!entry) {
    throw new ConvertSectionGroupError(
      'document entry is not defined',
      sectionGroupLabel,
    );
  }

  const page = getPageFromEntry(entry);

  if (!page) {
    throw new ConvertSectionGroupError(
      'document page is not defined',
      sectionGroupLabel,
    );
  }

  const sectionGroup = page[sectionGroupProp];

  if (!sectionGroup) {
    throw new ConvertSectionGroupError(
      'document section group is not defined',
      sectionGroupLabel,
    );
  }

  return convertSections(sectionGroup);
}
